import { GridColDef, GridComparatorFn, GridRowId, GridSortDirection, GridValidRowModel } from '@mui/x-data-grid';

const getDefaultSortComparator =
  <ValueType>(): GridComparatorFn<ValueType> =>
  (value1, value2) => {
    if (value1 == null && value2 != null) return -1;
    if (value1 != null && value2 == null) return 1;
    if (value1 == null && value2 == null) return 0;
    if (value1 instanceof Date && value2 instanceof Date) return value1.getTime() - value2.getTime();
    if (typeof value1 === 'string' && typeof value2 === 'string') return value1.localeCompare(value2);
    return Number(value1) - Number(value2);
  };

const getSortComparator = <RowType extends GridValidRowModel, ValueType>(
  column: GridColDef<RowType, ValueType>,
  sortDirection: GridSortDirection
): GridComparatorFn<ValueType> => {
  const customComparator = column.getSortComparator?.(sortDirection);
  if (customComparator) return customComparator;

  const sortComparator = column.sortComparator ?? getDefaultSortComparator<ValueType>();
  if (sortDirection === 'desc') {
    return (value1, value2, cellParams1, cellParams2) => -sortComparator(value1, value2, cellParams1, cellParams2);
  }
  return sortComparator;
};

export const withSelectedRowsFirst = <RowType extends GridValidRowModel>(
  columns: GridColDef<RowType>[],
  selectedIds: Set<GridRowId>
): GridColDef<RowType>[] =>
  columns.map((column) => {
    if (column.sortable === false) return column;

    return {
      ...column,
      getSortComparator: (sortDirection) => {
        const sortComparator = getSortComparator(column, sortDirection);

        return (value1, value2, cellParams1, cellParams2) => {
          const isFirstRowSelected = selectedIds.has(cellParams1.id);
          const isSecondRowSelected = selectedIds.has(cellParams2.id);

          if (isFirstRowSelected !== isSecondRowSelected) {
            return isFirstRowSelected ? -1 : 1;
          }

          return sortComparator(value1, value2, cellParams1, cellParams2);
        };
      }
    };
  });

/** Selected rows first (even when a filter hides them), followed by the filtered rows that are not selected. */
export const pinSelectedRows = <RowType extends GridValidRowModel & { id: string }>(
  allRows: RowType[],
  filteredRows: RowType[],
  selectedIds: Set<GridRowId>
): RowType[] => {
  const byId = new Map(allRows.map((row) => [row.id, row]));

  const selectedRows: RowType[] = [];
  selectedIds.forEach((id) => {
    const row = byId.get(String(id));
    if (row) selectedRows.push(row);
  });

  return selectedRows.concat(filteredRows.filter((row) => !selectedIds.has(row.id)));
};
